import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { publicUrl } from "@/lib/storage";
import FavoriteButton from "@/components/FavoriteButton";

type Team = {
  id: string;
  slug: string;
  name: string;
  sport: string | null;
  logo_path: string | null;
  description: string | null;
};

const Teams = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("All");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("teams")
        .select("id,slug,name,sport,logo_path,description")
        .eq("published", true)
        .order("sort_order");
      setTeams((data as any) ?? []);
      setLoading(false);
    })();
  }, []);

  const sports = useMemo(
    () => ["All", ...Array.from(new Set(teams.map((t) => t.sport).filter(Boolean) as string[]))],
    [teams],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return teams.filter((t) => {
      if (sport !== "All" && t.sport !== sport) return false;
      if (!s) return true;
      return [t.name, t.sport ?? ""].join(" ").toLowerCase().includes(s);
    });
  }, [teams, q, sport]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-16">
        <header className="mb-10 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            The clubs
          </span>
          <h1 className="text-5xl md:text-6xl mt-3">Teams.</h1>
          <p className="text-muted-foreground mt-3">
            Discover the teams behind our athletes.
          </p>
        </header>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="max-w-md w-full">
            <Input
              type="search"
              placeholder="Search teams…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {sports.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {sports.map((s) => (
                <button
                  key={s}
                  onClick={() => setSport(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    sport === s
                      ? "bg-secondary text-secondary-foreground border-secondary"
                      : "bg-background text-foreground border-border hover:border-secondary"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground">No teams yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((t) => (
              <Link
                key={t.id}
                to={`/team/${t.slug}`}
                className="group relative rounded-2xl bg-card shadow-card hover:shadow-bold transition-all duration-500 hover:-translate-y-1 p-5 flex flex-col items-center text-center"
              >
                <div className="absolute top-2 right-2">
                  <FavoriteButton kind="team" id={t.id} size="sm" />
                </div>
                <div className="w-24 h-24 rounded-full bg-muted overflow-hidden flex items-center justify-center mb-3">
                  {t.logo_path ? (
                    <img
                      src={publicUrl(t.logo_path)!}
                      alt={t.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="font-display text-2xl text-muted-foreground">
                      {t.name.charAt(0)}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg leading-tight">{t.name}</h3>
                {t.sport && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                    {t.sport}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Teams;
